import { useState, type FormEvent } from 'react';
import styled, { css } from 'styled-components';
import { useRateAssistantAnswerMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconThumbDown, IconThumbUp } from '@shared/ui/icons';
import {
  ASSISTANT_MAX_FEEDBACK_COMMENT,
  type AssistantFeedbackValue,
  type AssistantMessage,
} from '@entities/assistant/types';

const Wrap = styled.div`
  align-self: flex-start;
  max-width: 88%;
  margin-top: -${({ theme }) => theme.spacing(1.5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Toggle = styled.button<{ $on: boolean; $tone: 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
  ${({ $on, $tone, theme }) =>
    $on &&
    css`
      color: ${theme.colors.status[$tone]};
      border-color: ${theme.colors.status[$tone]};
    `}
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const CommentField = styled.textarea`
  min-height: 56px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.4;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  justify-content: flex-end;
`;

const Note = styled.span<{ $error?: boolean }>`
  font-size: 12px;
  color: ${({ $error, theme }) => ($error ? theme.colors.status.danger : theme.colors.text.muted)};
`;

/**
 * Оценка ответа Помощника: «полезно» / «не полезно» и необязательный
 * комментарий «что не так». Повторное нажатие на выбранную оценку её снимает.
 * Ответы «не полезно» вместе с вопросом видит администратор — по ним
 * исправляют справку Помощника.
 */
export function AnswerFeedback({ message }: { message: AssistantMessage }) {
  const [rate, { isLoading }] = useRateAssistantAnswerMutation();
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState(message.feedback_comment ?? '');
  const [error, setError] = useState<string | null>(null);
  const [thanked, setThanked] = useState(false);
  const current = message.feedback;

  async function save(value: AssistantFeedbackValue | null, text?: string) {
    setError(null);
    try {
      await rate({
        messageId: message.id,
        sessionId: message.session_id,
        value,
        comment: text,
      }).unwrap();
      return true;
    } catch {
      setError('Не удалось сохранить оценку — попробуйте ещё раз.');
      return false;
    }
  }

  async function pick(value: AssistantFeedbackValue) {
    setThanked(false);
    if (current === value) {
      setCommentOpen(false);
      setComment('');
      await save(null);
      return;
    }
    const ok = await save(value);
    if (ok) setCommentOpen(value === -1);
  }

  async function sendComment(e: FormEvent) {
    e.preventDefault();
    const ok = await save(-1, comment.trim() || undefined);
    if (ok) {
      setCommentOpen(false);
      setThanked(true);
    }
  }

  return (
    <Wrap>
      <Row>
        <span>Ответ помог?</span>
        <Toggle
          type="button"
          $on={current === 1}
          $tone="success"
          aria-pressed={current === 1}
          aria-label="Полезный ответ"
          title={current === 1 ? 'Снять оценку' : 'Полезно'}
          disabled={isLoading}
          onClick={() => void pick(1)}
        >
          <IconThumbUp size={15} />
        </Toggle>
        <Toggle
          type="button"
          $on={current === -1}
          $tone="danger"
          aria-pressed={current === -1}
          aria-label="Ответ не помог"
          title={current === -1 ? 'Снять оценку' : 'Не полезно'}
          disabled={isLoading}
          onClick={() => void pick(-1)}
        >
          <IconThumbDown size={15} />
        </Toggle>
        {current === -1 && !commentOpen && (
          <Button type="button" $variant="ghost" $size="sm" onClick={() => setCommentOpen(true)}>
            {message.feedback_comment ? 'Изменить комментарий' : 'Что не так?'}
          </Button>
        )}
        {thanked && <Note>Спасибо — администратор разберёт этот ответ.</Note>}
        {error && (
          <Note $error role="alert">
            {error}
          </Note>
        )}
      </Row>
      {commentOpen && (
        <CommentForm onSubmit={(e) => void sendComment(e)}>
          <CommentField
            autoFocus
            value={comment}
            maxLength={ASSISTANT_MAX_FEEDBACK_COMMENT}
            placeholder="Что не так? Например: не тот раздел, нет такой кнопки, устарело (необязательно)"
            aria-label="Что не так с ответом"
            onChange={(e) => setComment(e.target.value)}
          />
          <Actions>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={() => {
                setCommentOpen(false);
                // Правка уже оставленного комментария — просто закрыть форму.
                setThanked(!message.feedback_comment);
              }}
            >
              {message.feedback_comment ? 'Отмена' : 'Без комментария'}
            </Button>
            <Button type="submit" $size="sm" disabled={isLoading}>
              Отправить
            </Button>
          </Actions>
        </CommentForm>
      )}
    </Wrap>
  );
}
